import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { useAuth, canManageOps } from "@/lib/auth";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ScrollText, Plus, Trash2, GripVertical, CheckSquare, ToggleLeft, Hash, Type, PenTool, Camera, Thermometer, Save } from "lucide-react";

const ITEM_TYPES = [
  { value: "checkbox", label: "Checkbox", icon: CheckSquare },
  { value: "yes_no", label: "Yes / No", icon: ToggleLeft },
  { value: "number", label: "Number", icon: Hash },
  { value: "text", label: "Text", icon: Type },
  { value: "signature", label: "Signature", icon: PenTool },
  { value: "photo", label: "Photo", icon: Camera },
  { value: "temperature", label: "Temperature", icon: Thermometer },
];

function newItem() {
  return {
    id: crypto.randomUUID(),
    label: "",
    type: "checkbox",
    required: true,
    evidence_required: false,
    weighting: 1,
    notes_allowed: true,
  };
}

function Editor({ initial, onSave, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [desc, setDesc] = useState(initial?.description || "");
  const [cat, setCat] = useState(initial?.category || "Housekeeping");
  const [items, setItems] = useState(initial?.items?.length ? initial.items : [newItem()]);

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setItems(arr);
  };
  const patch = (id, upd) => setItems(items.map((x) => x.id === id ? { ...x, ...upd } : x));
  const del = (id) => setItems(items.filter((x) => x.id !== id));

  const save = () => {
    if (!name) return toast.error("Name required");
    if (items.some((i) => !i.label)) return toast.error("Every item needs a label");
    onSave({ name, description: desc, category: cat, items });
  };

  return (
    <div className="space-y-6" data-testid="checklist-editor">
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name" placeholder="Guest Bathroom Deep Clean" /></div>
        <div><Label>Category</Label>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Housekeeping","Food Safety","Maintenance","Front of House","Compliance","Other"].map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Description</Label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} /></div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps text-muted-foreground">Checklist items</div>
          <Button size="sm" variant="outline" onClick={() => setItems([...items, newItem()])} data-testid="add-item">
            <Plus className="w-3 h-3 mr-1" />Add item
          </Button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => {
            const TypeIcon = ITEM_TYPES.find((t) => t.value === it.type)?.icon || CheckSquare;
            return (
              <div key={it.id} className="border border-border bg-card p-4 flex items-start gap-3" data-testid={`item-${i}`}>
                <div className="flex flex-col items-center gap-1 pt-1 text-muted-foreground">
                  <button onClick={() => move(i, -1)} className="hover:text-foreground" aria-label="move up">▲</button>
                  <GripVertical className="w-3 h-3" />
                  <button onClick={() => move(i, 1)} className="hover:text-foreground" aria-label="move down">▼</button>
                </div>
                <div className="flex-1 grid md:grid-cols-[1fr_160px] gap-3">
                  <div>
                    <Input
                      value={it.label}
                      onChange={(e) => patch(it.id, { label: e.target.value })}
                      placeholder="Label (e.g. Toilet sanitized)"
                      data-testid={`item-label-${i}`}
                    />
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <label className="flex items-center gap-1.5">
                        <Switch checked={it.required} onCheckedChange={(v) => patch(it.id, { required: v })} /> Required
                      </label>
                      <label className="flex items-center gap-1.5">
                        <Switch checked={it.evidence_required} onCheckedChange={(v) => patch(it.id, { evidence_required: v })} /> Photo evidence
                      </label>
                      <label className="flex items-center gap-1.5">
                        Weight
                        <Input type="number" className="w-14 h-7" value={it.weighting} onChange={(e) => patch(it.id, { weighting: Number(e.target.value) })} />
                      </label>
                    </div>
                  </div>
                  <div>
                    <Select value={it.type} onValueChange={(v) => patch(it.id, { type: v })}>
                      <SelectTrigger>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="w-3.5 h-3.5" />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {ITEM_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            <div className="flex items-center gap-2"><t.icon className="w-3.5 h-3.5" />{t.label}</div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(it.id)}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={save} data-testid="template-save"><Save className="w-4 h-4 mr-1" />Save template</Button>
      </div>
    </div>
  );
}

export default function Checklists() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const manage = canManageOps(user);
  const [editing, setEditing] = useState(null); // template object or "new"

  const { data: templates } = useQuery({
    queryKey: ["checklists"],
    queryFn: async () => (await api.get("/checklists")).data,
  });

  const save = async (payload) => {
    try {
      if (editing === "new") {
        await api.post("/checklists", payload);
        toast.success("Template created");
      } else {
        await api.patch(`/checklists/${editing.id}`, payload);
        toast.success("Template updated");
      }
      qc.invalidateQueries({ queryKey: ["checklists"] });
      setEditing(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Archive this template?")) return;
    try {
      await api.delete(`/checklists/${id}`);
      toast.success("Archived");
      qc.invalidateQueries({ queryKey: ["checklists"] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Configure</div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Checklists</h1>
          <p className="text-sm text-muted-foreground mt-2">Reusable templates that power every recurring task.</p>
        </div>
        {manage && <Button onClick={() => setEditing("new")} data-testid="new-template-btn"><Plus className="w-4 h-4 mr-1" />New Template</Button>}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {templates?.filter((t) => t.is_active).map((t) => (
          <div key={t.id} className="border border-border bg-card rounded-2xl p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-lg transition-all" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="label-caps text-muted-foreground">{t.category || "Template"}</div>
                <div className="font-display text-lg font-semibold mt-1 truncate">{t.name}</div>
              </div>
              <Badge variant="outline" className="shrink-0">v{t.version}</Badge>
            </div>
            {t.description && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{t.description}</p>}
            <div className="mt-3 flex flex-wrap gap-1">
              {t.items?.slice(0, 5).map((i, idx) => (
                <span key={i.id || `${t.id}-item-${idx}`} className="text-[0.65rem] px-2 py-0.5 bg-muted rounded-sm">{i.label}</span>
              ))}
              {t.items?.length > 5 && <span className="text-[0.65rem] px-2 py-0.5 bg-muted rounded-sm">+{t.items.length - 5}</span>}
            </div>
            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{t.items?.length || 0} items</span>
              {manage && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(t)} data-testid={`edit-template-${t.id}`}>Edit</Button>
                  <Button size="icon" variant="ghost" onClick={() => del(t.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        ))}
        {templates?.length === 0 && (
          <div className="col-span-full border border-dashed border-border p-12 text-center">
            <ScrollText className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <div className="font-medium">No templates yet</div>
          </div>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "New template" : "Edit template"}</DialogTitle>
          </DialogHeader>
          {editing && <Editor initial={editing === "new" ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
